import { useAPI } from "kw/api/useAPI";
import { $ } from "kw/api";
import { useState } from "react";
import { RiskStateFilterEnum } from "kw/api/zeus";
import { deref } from "kanopi-components/utils";
import { substituteRiskData } from "kw/util/substituteRiskData";
import { jsonStringToCamelCase } from "kanopi-components/utils/json";

export const usePolicies = () => {
  const [policies, setPolicies] = useState<TPolicies>();
  const [policy, setPolicy] = useState<TPolicy>();
  const { query, loading } = useAPI();
  const fetchPolicies = (search = "", page: number) => {
    query(
      policiesQuery,
      (response: { admin: { policies: TPolicies } }) => {
        setPolicies({
          ...response.admin.policies,
          items: response.admin.policies.items.map((policy) => ({
            ...policy,
            riskProfile: {
              ...policy?.riskProfile,
              riskData: jsonStringToCamelCase(policy?.riskProfile?.riskData),
            },
          })),
        });
      },
      {
        search,
        page,
      }
    );
  };

  const searchPolicies = (search = "", page = 1) => {
    query(
      policiesQuery,
      (response: { admin: { policies: TPolicies } }) => {
        setPolicies(response.admin.policies);
      },
      {
        search: search,
        page: page,
      }
    );
  };

  const fetchPolicy = (id: string) => {
    query(
      policyQuery,
      (response: { admin: { policy: TPolicy } }) => {
        let _policy = response.admin.policy;
        const questionSets = deref(
          response.admin.policy,
          "riskProfile.riskStateQuestionSets",
          []
        );
        const results = [];
        let finalRiskProfile;
        for (const questionSet of questionSets) {
          const [enquiry, riskProfile] = substituteRiskData(
            {
              questionSet,
            },
            deref(response.admin.policy, "riskProfile", {})
          );
          results.push(enquiry.questionSet);
          finalRiskProfile = riskProfile;
          finalRiskProfile = { ...finalRiskProfile, riskStateQuestionSets: results };
        }
        _policy = {
          ...response.admin.policy,
          ...{ riskProfile: finalRiskProfile },
        };

        setPolicy(_policy);
      },
      {
        id,
        filterBy: RiskStateFilterEnum.ALL,
      }
    );
  };

  return {
    policies,
    policy,
    loading,
    fetchPolicies,
    fetchPolicy,
    searchPolicies,
  };
};

const policiesQuery = {
  admin: {
    policies: [
      {
        search: $("search", "String!"),
        page: $("page", "Int!"),
      },
      {
        totalItemCount: true,
        page: true,
        pageCount: true,
        pageSize: true,
        hasNext: true,
        hasPrev: true,
        items: {
          id: true,
          policyNumber: true,
          startDate: true,
          finishDate: true,
          status: true,
          statusDisplay: true,
          quote: { postTaxPremium: true },
          riskProfile: { owner: { firstName: true, lastName: true }, riskData: true },
        },
      },
    ],
  },
};

const policyQuery = {
  admin: {
    policy: [
      {
        id: $("id", "ID!"),
      },
      {
        policyNumber: true,
        startDate: true,
        finishDate: true,
        isPendingCancellation: true,
        status: true,
        statusDisplay: true,
        documents: {
          name: true,
          type: true,
          url: true,
          created: true,
        },
        riskProfile: {
          id: true,
          owner: {
            id: true,
            firstName: true,
            lastName: true,
            isActive: true,
          },
          data: true,
          riskData: true,
          riskStateQuestionSets: [
            {
              filterBy: $("filterBy", "RiskStateFilterEnum!"),
            },
            {
              id: true,
              title: true,
              description: true,
              key: true,
              questions: {
                id: true,
                help: true,
                label: true,
                inputType: true,
                inputArgs: true,
                storagePath: true,
                order: true,
                validators: true,
                default: true,
              },
              order: true,
            },
          ],
        },
        quote: {
          id: true,
          covers: {
            key: true,
            title: true,
            description: true,
          },
          product: {
            name: true,
            provider: {
              name: true,
            },
            availableCovers: {
              key: true,
              title: true,
              description: true,
            },
          },
          preTaxPremium: true,
          postTaxPremium: true,
          amountDue: true,
          paymentFrequency: true,
          stateDisplay: true,
          createdTimestamp: true,
          updatedTimestamp: true,
          commission: true,
          fees: {
            breakdown: {
              feeType: {
                key: true,
                label: true,
              },
              amount: true,
            },
          },
        },
        transactions: {
          id: true,
          statusDisplay: true,
          transactionTypeDisplay: true,
          actionTypeDisplay: true,
          amount: true,
          policyNumber: true,
          paymentProfile: {
            customerReference: true,
          },
        },
        latestRenewal: {
          id: true,
          canAccept: true,
          quote: {
            id: true,
            isBound: true,
            covers: {
              key: true,
              title: true,
              description: true,
            },
            product: {
              name: true,
              provider: {
                name: true,
              },
              availableCovers: {
                key: true,
                title: true,
                description: true,
              },
            },
            preTaxPremium: true,
            postTaxPremium: true,
            amountDue: true,
            paymentFrequency: true,
            stateDisplay: true,
            state: true,
            createdTimestamp: true,
            updatedTimestamp: true,
            commission: true,
            fees: {
              breakdown: {
                feeType: {
                  key: true,
                },
                amount: true,
              },
            },
          },
        },
        latestCancellation: {
          effectiveDate: true,
          data: true,
          amountDue: true,
          fees: {
            breakdown: {
              feeType: {
                key: true,
              },
              kind: true,
              amount: true,
            },
          },
        },
        policyPeriods: {
          startDate: true,
          finishDate: true,
          quote: {
            id: true,
            amountDue: true,
          },
          documents: {
            name: true,
            type: true,
            url: true,
            created: true,
          },
        },
      },
    ],
  },
};
